import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';

import firebase from '../../../Firebase/Firebase';
import { configureFrenchAuthEmails } from '../../../Utils/authEmailActions';

const maskedEmail = (email) => {
  const [name = '', domain = ''] = String(email || '').split('@');
  if (!domain) return '';
  const visible = name.slice(0, Math.min(2, name.length));
  return `${visible}${'•'.repeat(Math.max(3, name.length - visible.length))}@${domain}`;
};

const safeContinueUrl = (value) => {
  if (!value || typeof window === 'undefined') return '/login';
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : '/login';
  } catch (_) {
    return '/login';
  }
};

const friendlyError = (error) => {
  const code = String(error?.code || '');
  if (code.includes('expired-action-code')) return 'Ce lien a expiré. Demandez un nouveau message depuis Le Petit Boulot.';
  if (code.includes('invalid-action-code')) return 'Ce lien a déjà été utilisé ou n’est plus valide.';
  if (code.includes('weak-password')) return 'Choisissez un mot de passe d’au moins 8 caractères.';
  if (code.includes('user-disabled')) return 'Ce compte a été désactivé. Contactez l’assistance.';
  return 'Cette opération sécurisée n’a pas pu être terminée. Demandez un nouveau lien.';
};

const AuthActionHandler = () => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const mode = params.get('mode') || '';
  const actionCode = params.get('oobCode') || '';
  const continueUrl = safeContinueUrl(params.get('continueUrl'));

  const [state, setState] = useState('checking');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    configureFrenchAuthEmails();

    const check = async () => {
      if (!actionCode || !['resetPassword', 'verifyEmail', 'recoverEmail'].includes(mode)) {
        if (mounted) {
          setError('Le lien reçu est incomplet. Demandez un nouveau message depuis l’application.');
          setState('error');
        }
        return;
      }

      try {
        if (mode === 'resetPassword') {
          const accountEmail = await firebase.auth().verifyPasswordResetCode(actionCode);
          if (!mounted) return;
          setEmail(accountEmail || '');
          setState('reset-ready');
          return;
        }

        if (mode === 'verifyEmail') {
          await firebase.auth().applyActionCode(actionCode);
          await firebase.auth().currentUser?.reload?.().catch(() => undefined);
          if (mounted) setState('verified');
          return;
        }

        const info = await firebase.auth().checkActionCode(actionCode);
        await firebase.auth().applyActionCode(actionCode);
        if (!mounted) return;
        setEmail(info?.data?.email || '');
        setState('recovered');
      } catch (nextError) {
        if (!mounted) return;
        setError(friendlyError(nextError));
        setState('error');
      }
    };

    check();
    return () => { mounted = false; };
  }, [actionCode, mode]);

  const resetPassword = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (password.length < 8) {
      setError('Choisissez un mot de passe d’au moins 8 caractères.');
      return;
    }
    if (password !== confirmation) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setBusy(true);
    try {
      await firebase.auth().confirmPasswordReset(actionCode, password);
      setPassword('');
      setConfirmation('');
      setState('password-updated');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === 'resetPassword'
    ? 'Réinitialiser votre mot de passe'
    : mode === 'verifyEmail'
      ? 'Vérification de votre adresse e-mail'
      : 'Sécurisation de votre adresse e-mail';

  return (
    <Page>
      <Card>
        <Brand><Monogram>PB</Monogram><div><strong>Le Petit Boulot</strong><span>Espace de sécurité du compte</span></div></Brand>
        <Heading>
          {mode === 'resetPassword' ? <LockResetOutlinedIcon /> : <MarkEmailReadOutlinedIcon />}
          <div><h1>{title}</h1><p>Cette page sécurisée appartient à <b>lepetitboulot.fr</b>.</p></div>
        </Heading>

        {state === 'checking' && <Status>Vérification du lien sécurisé…</Status>}

        {state === 'reset-ready' && (
          <form onSubmit={resetPassword}>
            <Intro>Compte concerné : <strong>{maskedEmail(email)}</strong></Intro>
            <Field><span>Nouveau mot de passe</span><input type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength="8" required /></Field>
            <Field><span>Confirmer le mot de passe</span><input type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} minLength="8" required /></Field>
            {error && <Alert role="alert"><ErrorOutlineOutlinedIcon />{error}</Alert>}
            <Primary type="submit" disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer le nouveau mot de passe'}</Primary>
          </form>
        )}

        {state === 'verified' && (
          <Success><CheckCircleOutlineOutlinedIcon /><div><strong>Adresse e-mail vérifiée</strong><span>Votre compte peut maintenant être utilisé normalement.</span></div></Success>
        )}

        {state === 'recovered' && (
          <Success><CheckCircleOutlineOutlinedIcon /><div><strong>Adresse e-mail restaurée</strong><span>{maskedEmail(email) || 'Votre adresse précédente'} a été rétablie.</span></div></Success>
        )}

        {state === 'password-updated' && (
          <Success><CheckCircleOutlineOutlinedIcon /><div><strong>Mot de passe modifié</strong><span>Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</span></div></Success>
        )}

        {state === 'error' && <Alert role="alert"><ErrorOutlineOutlinedIcon />{error}</Alert>}

        {state !== 'checking' && state !== 'reset-ready' && (
          <PrimaryLink to={continueUrl || '/login'}>Continuer vers Le Petit Boulot</PrimaryLink>
        )}
        <SecondaryLink as={Link} to="/login">Retour à la connexion</SecondaryLink>
        <Footnote>Le Petit Boulot ne vous demandera jamais votre mot de passe par e-mail.</Footnote>
      </Card>
    </Page>
  );
};

const Page = styled.main`min-height:calc(100vh - 4.6rem);display:grid;place-items:center;padding:clamp(1rem,4vw,3rem);background:#f5f3ef;`;
const Card = styled.section`width:min(34rem,100%);display:grid;gap:1rem;padding:clamp(1.1rem,4vw,2rem);border:1px solid #ded8ce;border-radius:1.25rem;background:#fff;box-shadow:0 1.5rem 4rem rgba(0,0,0,.1);`;
const Brand = styled.div`display:flex;align-items:center;gap:.65rem;padding-bottom:.9rem;border-bottom:1px solid #eee9e1;div{display:grid;gap:.05rem;}strong{font-size:.9rem;}span{color:#746e66;font-size:.68rem;}`;
const Monogram = styled.span`width:2.55rem;height:2.55rem;display:grid;place-items:center;border-radius:.75rem;background:#111;color:#fff;font-size:.82rem;font-weight:900;letter-spacing:-.08em;`;
const Heading = styled.header`display:flex;align-items:flex-start;gap:.7rem;>svg{flex:0 0 auto;width:2.5rem;height:2.5rem;padding:.55rem;border-radius:.75rem;background:#111;color:#fff;}div{display:grid;gap:.2rem;}h1{margin:0;font-size:clamp(1.25rem,4vw,1.75rem);line-height:1.15;}p{margin:0;color:#716b63;font-size:.76rem;line-height:1.5;}`;
const Status = styled.p`margin:0;padding:.8rem;border-radius:.8rem;background:#f5f2ed;color:#5f5951;font-size:.78rem;text-align:center;`;
const Intro = styled.p`margin:0 0 .8rem;color:#625c54;font-size:.76rem;`;
const Field = styled.label`display:grid;gap:.35rem;margin-top:.7rem;span{font-size:.72rem;font-weight:800;}input{min-height:3rem;padding:.7rem .8rem;border:1px solid #dcd4c8;border-radius:.75rem;font:inherit;font-size:.82rem;&:focus{outline:2px solid #c0a375;outline-offset:1px;}}`;
const Primary = styled.button`width:100%;min-height:3rem;margin-top:.9rem;border:0;border-radius:999px;background:#111;color:#fff;font:inherit;font-size:.78rem;font-weight:850;cursor:pointer;&:disabled{opacity:.55;cursor:wait;}`;
const PrimaryLink = styled(Link)`min-height:3rem;display:flex;align-items:center;justify-content:center;padding:.7rem .9rem;border-radius:999px;background:#111;color:#fff;font-size:.78rem;font-weight:850;text-decoration:none;`;
const SecondaryLink = styled.a`justify-self:center;color:#5e574f;font-size:.72rem;font-weight:750;text-decoration:underline;`;
const Alert = styled.div`display:flex;align-items:flex-start;gap:.5rem;padding:.75rem .8rem;border:1px solid #efc9c4;border-radius:.78rem;background:#fff4f2;color:#8e3029;font-size:.74rem;line-height:1.45;svg{flex:0 0 auto;font-size:1rem;margin-top:.08rem;}`;
const Success = styled.div`display:flex;align-items:flex-start;gap:.65rem;padding:.85rem;border:1px solid #c9e8d4;border-radius:.85rem;background:#edf9f1;color:#17623a;>svg{flex:0 0 auto;}div{display:grid;gap:.15rem;}strong{font-size:.85rem;}span{font-size:.72rem;line-height:1.45;}`;
const Footnote = styled.small`color:#7c756c;font-size:.62rem;line-height:1.45;text-align:center;`;

export default AuthActionHandler;
